import type { ReactNode, RefObject } from 'react';
import React, { useRef, Fragment } from 'react';
import { Popover as HeadlessPopover, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

interface PopoverProps {
  children?: ReactNode;
  className?: string;
  button: ReactNode;
  panelClassName?: string;
  position?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  buttonClassName?: string;
  hideOnClick?: boolean;
}

const Popover: React.FC<PopoverProps> = (props) => {
  const { position, align, hideOnClick = true } = props;

  const popoverClassNames = twMerge(
    'relative flex flex-col justify-center',
    align === 'center' && 'items-center',
    align === 'end' && 'items-end',
    props.className,
  );

  const panelClassNames = twMerge(
    'bg-white flex absolute  z-10',
    position === 'top' && 'bottom-full mb-3',
    position !== 'top' && 'top-full mt-3',
    props.panelClassName,
  );

  const buttonRef: RefObject<HTMLDivElement> = useRef(null);

  const hidePopover = () => {
    if (!hideOnClick) return;

    buttonRef.current?.click();
  };

  return (
    <HeadlessPopover className={popoverClassNames}>
      <HeadlessPopover.Button
        as="div"
        ref={buttonRef}
        className={twMerge(props.buttonClassName)}
      >
        {props.button}
      </HeadlessPopover.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <HeadlessPopover.Panel
          className={panelClassNames}
          onClick={hidePopover}
        >
          {props.children}
        </HeadlessPopover.Panel>
      </Transition>
    </HeadlessPopover>
  );
};

export default Popover;
